import { FC, SyntheticEvent, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Box, Button, Divider, LinearProgress, Tab, Tabs } from '@mui/material'
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid'

import { colors } from '../../consts/consts'
import { CustomTraceEvent } from '../../types/Api'
import { Sort } from '../../types/Table'
import { getPagination } from '../../utils/pagination'
import JsonViewModal from '../JsonViewModal'
import NoDataInDataGridTable from './NoDataInTable'

interface IProps {
  handleDownload: () => void
  data: any
  isLoading: boolean
  isFiltered: boolean
}

const PAGE_SIZE_OPTION = [25, 50, 100]

function formatFuncArguments(row: any): string {
  const args = row?.args
  if (!args?.length) {
    return '-'
  }
  return args.map((a: any) => `${a.value}  [${a.type.toLowerCase()}]`).join(', ')
}

const Table: FC<IProps> = ({ handleDownload, data, isLoading, isFiltered }) => {
  const [modal, setModal] = useState<boolean>(false)
  const [currentRow, setCurrentRow] = useState<CustomTraceEvent | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const setSortToUrl = (sort: Sort) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set('sortField', sort.sortField)
    params.set('sortDir', sort.sortDir || 'asc')
    params.delete('page')
    setSearchParams(params)
  }

  const setPaginationToUrl = (pagination: GridPaginationModel) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set('page', pagination.page.toString())
    params.set('pageSize', pagination.pageSize.toString())
    setSearchParams(params)
  }

  const getColorByType = (type: number): string => {
    return colors[type] || '#ffffff'
  }

  const handleChange = (_event: SyntheticEvent, newValue: 'all' | 'known' | 'unknown') => {
    const params = new URLSearchParams(searchParams.toString())
    params.delete('page')
    params.set('known', newValue)
    setSearchParams(params)
  }

  const columns: GridColDef<any>[] = useMemo(
    () => [
      {
        field: 'identifier',
        headerName: 'Identifier',
        flex: 10,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (_, row) => row?.identifier ?? '-',
      },
      {
        field: 'func_arguments',
        headerName: 'Arguments',
        flex: 10,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (_, row) => formatFuncArguments(row),
        renderCell: (params) => {
          const argsText = formatFuncArguments(params.row)
          const desc = params.row?.description
          if (!desc) {
            return <span>{argsText}</span>
          }
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 0.5,
                width: '100%',
                minHeight: '100%',
                textAlign: 'center',
              }}
            >
              <Box component="span" sx={{ lineHeight: 1.25, fontSize: '12px' }}>
                {argsText}
              </Box>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  px: 1,
                  py: 0.35,
                  borderRadius: 999,
                  bgcolor: '#fff9c4',
                  color: 'text.secondary',
                  fontSize: '11px',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                }}
              >
                {desc}
              </Box>
            </Box>
          )
        },
      },
      {
        field: 'return_value',
        headerName: 'Return Value',
        flex: 10,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (_, row) =>
          row?.return_value
            ? `${row?.return_value.value}  [${row?.return_value.type.toLowerCase()}]`
            : '-',
      },
      // {
      //   field: 'origin',
      //   headerName: 'Origin',
      //   flex: 10,
      //   headerAlign: 'center',
      //   align: 'center',
      //   valueGetter: (_, row) => row?.args?.high_entropy_api?.execution_context?.origin ?? '-',
      // },
      // {
      //   field: 'filename',
      //   headerName: 'Filename',
      //   flex: 10,
      //   headerAlign: 'center',
      //   align: 'center',
      //   valueGetter: (_, row) => row?.args?.high_entropy_api?.source_location?.url ?? '-',
      // },
      {
        field: 'name',
        headerName: 'Names',
        flex: 10,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (_, row) => {
          const attrs = row?.determined_attrs
          return attrs?.map((a: any) => a.name).join(', ') ?? '-'
        },
      },
      {
        field: 'url',
        headerName: 'Url',
        flex: 10,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (_, row) => row?.location?.url ?? '-',
      },
      {
        field: 'actions',
        type: 'actions',
        align: 'center',
        flex: 1,
        minWidth: 88,
        getActions: (params: GridRowParams<any>) => {
          return [
            <GridActionsCellItem
              key={params.id}
              onClick={() => {
                setCurrentRow(params.row)
                setModal(true)
              }}
              icon={<VisibilityIcon />}
              label="View"
              title="View"
            />,
            <GridActionsCellItem
              key={params.id}
              onClick={() => {
                alert('Method not implemented')
              }}
              icon={<DeleteIcon />}
              label="Delete"
              title="Delete"
            />,
          ]
        },
      },
    ],
    []
  )

  return (
    <Box
      sx={{
        maxHeight: '1000px',
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Box sx={{ ml: '24px' }}>
        <Tabs
          value={searchParams.get('known') || 'all'}
          onChange={handleChange}
          aria-label="section tabs"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab disabled={!data} label="All" value={'all'} />
          <Tab disabled={!data} label="Known" value={'known'} />
          <Tab disabled={!data} label="Unknown" value={'unknown'} />
        </Tabs>
      </Box>
      <DataGrid
        rows={
          data?.trace_events?.map((row: CustomTraceEvent, index: number) => ({
            ...row,
            id: index,
          })) || []
        }
        columns={columns}
        columnHeaderHeight={36}
        disableColumnMenu={true}
        rowSelection={true}
        getRowHeight={() => 'auto'}
        getRowClassName={(params) => {
          const attrs = params.row.determined_attrs?.[0]
          const typeClass = attrs?.type != null ? `row-type-${attrs.type}` : ''
          const boldClass = attrs?.relevant ? 'row-bold' : ''
          const tooltipClass = attrs?.name ? 'row-with-tooltip' : ''
          return `${typeClass} ${boldClass} ${tooltipClass}`.trim()
        }}
        loading={isLoading}
        disableRowSelectionOnClick={true}
        sortingMode="server"
        paginationMode="server"
        rowCount={data?.meta?.page?.total || 0}
        paginationModel={getPagination(searchParams)}
        pagination
        onPaginationModelChange={setPaginationToUrl}
        sortingOrder={['asc', 'desc']}
        onSortModelChange={(sortModel: GridSortModel) => {
          if (sortModel.length) {
            setSortToUrl({ sortField: sortModel[0].field, sortDir: sortModel[0].sort || 'asc' })
          }
        }}
        slots={{
          loadingOverlay: () => <LinearProgress />,
          noRowsOverlay: () => <NoDataInDataGridTable isFiltered={isFiltered} />,
          toolbar: () => (
            <Box>
              <GridToolbarContainer sx={{ ml: '24px' }}>
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />

                <Button
                  variant="outlined"
                  sx={{
                    border: 'none',
                    fontSize: '13px',
                    px: '2px',
                    '&.Mui-disabled': { border: 'none' },
                  }}
                  onClick={handleDownload}
                  disabled={!data && !searchParams.get('nameOfFile')}
                >
                  <DownloadIcon fontSize={'small'} />
                  Download Filtered Data
                </Button>
              </GridToolbarContainer>
              <Divider />
            </Box>
          ),
        }}
        pageSizeOptions={PAGE_SIZE_OPTION}
        sx={{
          borderRadius: '14px',
          border: 'none',
          background: '#fffffff',
          overflow: 'visible',
          height: '100%',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '12px',
            fontWeight: 600,
          },
          '& .MuiDataGrid-columnSeparator': {
            color: 'transparent',
          },
          '& .MuiDataGrid-iconButtonContainer': {
            position: 'absolute',
            right: '0px',
          },
          '& .MuiDataGrid-cellContent': {
            color: 'text.primary',
          },
          '& .MuiDataGrid-sortIcon': {
            opacity: 'inherit',
          },
          '& .MuiDataGrid-row:hover': {
            bgcolor: 'gray.light',
          },
          '& .MuiDataGrid-cell': {
            borderRight: 1,
            borderColor: 'gray.light',
            fontSize: '12px',
          },
          '& .MuiDataGrid-cell:last-of-type': {
            borderRight: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: 'tableHeader.main',
            borderRight: 1,
            borderBottom: 1,
            borderColor: 'gray.light',
          },
          '& .MuiDataGrid-columnHeader--last': {
            borderRight: 0,
          },
          '& .MuiDataGrid-withBorderColor': {
            borderColor: 'grey.light',
          },
          '& .MuiDataGrid-columnHeader--emptyGroup': {
            borderBottom: 1,
            borderColor: 'gray.light',
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: 'transparent',
            border: 'none',
          },
          '& .MuiDataGrid-main': {
            backgroundColor: 'background.paper',
            borderRadius: 2,
          },
          '& .MuiDataGrid-scrollbarFiller--header': {
            backgroundColor: 'tableHeader.main',
          },
          ...Object.entries(colors).reduce(
            (acc, [type, color]) => ({
              ...acc,
              [`& .row-type-${type}`]: { backgroundColor: color },
            }),
            {}
          ),
          '& .row-bold': {
            fontWeight: 900,
          },
          minHeight: '200px',
        }}
      />
      <JsonViewModal modal={modal} setModal={setModal} jsonData={currentRow} />
    </Box>
  )
}

export default Table
